import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const FamilialHeartDiseases = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'Hypertrophic Cardiomyopathy Explained',
      duration: '10:22',
      views: '19K views',
      thumbnail: 'https://public.readdy.ai/ai/img_res/df021f520852c695f7982a9028cad5da.jpg',
    },
    {
      title: 'Living with Long QT Syndrome',
      duration: '7:15',
      views: '14K views',
      thumbnail: 'https://readdy.ai/api/search-image?query=patient%20sharing%20personal%20story%20about%20inherited%20heart%20rhythm%20disorder%20in%20comfortable%20interview%20setting%20warm%20natural%20lighting%20cream%20tones&width=400&height=250&seq=fhd-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Genetic Testing for Heart Disease',
      duration: '13:40',
      views: '27K views',
      thumbnail: 'https://readdy.ai/api/search-image?query=genetic%20counselor%20reviewing%20DNA%20test%20results%20for%20cardiac%20conditions%20with%20patient%20in%20modern%20medical%20office%20professional%20healthcare%20photography&width=400&height=250&seq=fhd-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Genetic Heart Health Education Series
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Familial Heart Diseases
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Familial heart diseases are inherited cardiovascular conditions passed through generations. Learn about their causes, warning signs, and how early screening and treatment can protect your family.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=medical%20professional%20writing%20research%20article%20about%20inherited%20heart%20conditions%20on%20laptop%20with%20stethoscope%20and%20cardiac%20diagrams%20on%20desk%20warm%20natural%20lighting%20clean%20modern%20workspace%20healthcare%20content%20creation&width=400&height=300&seq=fhd-blog-card&orientation=landscape"
                  alt="Blog articles about familial heart diseases"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog'
                      ? 'bg-[#f04e58]'
                      : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog'
                        ? 'text-white'
                        : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth articles about familial heart diseases</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog'
                      ? 'bg-[#f04e58] rotate-45'
                      : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=cardiologist%20recording%20educational%20video%20about%20hereditary%20heart%20disease%20in%20modern%20hospital%20studio%20with%20ECG%20monitors%20and%20heart%20anatomy%20models%20professional%20medical%20video%20production%20warm%20lighting&width=400&height=300&seq=fhd-video-card&orientation=landscape"
                  alt="Educational videos about familial heart diseases"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos'
                      ? 'bg-[#f04e58]'
                      : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos'
                        ? 'text-white'
                        : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Educational video content</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos'
                      ? 'bg-[#f04e58] rotate-45'
                      : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=colorful%20medical%20infographic%20about%20inherited%20cardiac%20conditions%20displayed%20on%20large%20screen%20showing%20heart%20anatomy%20charts%20family%20genetics%20diagrams%20and%20risk%20statistics%20modern%20data%20visualization%20coral%20and%20teal%20accents&width=400&height=300&seq=fhd-infograph-card&orientation=landscape"
                  alt="Infographics about familial heart diseases"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph'
                      ? 'bg-[#f04e58]'
                      : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph'
                        ? 'text-white'
                        : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Visual guides and data</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph'
                      ? 'bg-[#f04e58] rotate-45'
                      : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Is a Heart Condition Hiding in Your DNA? Four Surprising Truths
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 10, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  8 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=detailed%20anatomical%20illustration%20of%20human%20heart%20with%20highlighted%20genetic%20pathways%20showing%20inherited%20cardiac%20conditions%20warm%20coral%20and%20cream%20color%20palette%20professional%20medical%20illustration%20clean%20modern%20aesthetic&width=800&height=400&seq=fhd-blog-hero&orientation=landscape"
                  alt="Familial Heart Diseases"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  When we think of heart disease, we often picture risks related to diet, exercise, and lifestyle. But for millions of people, the greatest risk isn't about choices—it's written in their DNA from the day they were born. These familial heart diseases are inherited conditions that can silently affect anyone, including the young and seemingly healthy.
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's Far More Common Than You Think
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Familial Hypercholesterolemia (FH) is a genetic disorder that causes extremely high levels of "bad" (LDL) cholesterol from birth. This isn't a condition developed over time; it's a lifelong burden on the cardiovascular system. The most surprising fact is its frequency: <strong>FH affects approximately 1 in 250 people worldwide</strong>. To put that in perspective, it's about twice as common as Type 1 diabetes in adults, yet receives far less public attention. The early and persistent buildup of cholesterol can lead to heart attacks or strokes at a young age.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It Can Affect Seemingly Healthy Young People and Athletes
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While there are several types of inherited heart diseases, some of the most dangerous show no obvious symptoms until a major cardiac event occurs. Two such conditions are <strong>Familial Hypertrophic Cardiomyopathy (HCM)</strong>, where the heart muscle thickens abnormally, and <strong>Familial Arrhythmogenic Right Ventricular Cardiomyopathy (ARVC)</strong>, where healthy heart muscle is replaced by fatty tissue, disrupting the heart's electrical signals and leading to life-threatening arrhythmias.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <p className="text-gray-900 font-semibold mb-2">Critical Warning:</p>
                  <p className="text-gray-700 leading-relaxed">
                    The most devastating effect of these conditions is the risk of sudden cardiac arrest or death, which can strike even young people or athletes during exercise.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Risk Can Be a 50/50 Coin Flip
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Many of these conditions follow a clear genetic pattern. Familial Hypertrophic Cardiomyopathy (HCM), for example, is typically an autosomal dominant disorder. This means that if one parent carries the genetic mutation for the condition, <strong>each of their children has a 50% chance of inheriting it</strong>.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#f04e58]">50%</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">Inheritance Risk</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  This genetic coin flip transforms a personal health diagnosis into a critical, multi-generational family concern and highlights the profound importance of sharing medical information with relatives.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    Your Family History Can Save Lives
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The power of knowing your family's health history cannot be overstated. Once one person in a family is diagnosed with an inherited heart condition, it creates a critical opportunity for predictive screening and preventive care for their relatives. Modern genetic testing can identify at-risk family members before they ever show symptoms, allowing for early intervention. This proactive approach is the foundation of precision cardiology.
                </p>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white mb-4">
                  <p className="font-semibold mb-2 text-white">Critical Action:</p>
                  <p className="text-white/95 leading-relaxed">
                    If one person in a family is diagnosed, all first-degree relatives should be screened.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  While inheriting a risk for heart disease can be unsettling, learning about it is the first step toward rewriting your family's medical future. A diagnosis for one person becomes a life-saving map for others, guiding them toward the screening and preventive care that defines modern cardiology.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Do You Know Your Heart's Family Story?
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Understanding your family's cardiac history could be the difference between prevention and crisis. If heart disease runs in your family, don't wait for symptoms—talk to your doctor about genetic screening today.
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Protect Your Heart, Protect Your Family</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  Early screening for familial heart diseases can save lives. If you have a family history of heart conditions, take the first step toward understanding your risk today.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Familial Heart Diseases: What You Need to Know
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this comprehensive video to learn about inherited heart conditions, their genetic basis, and the importance of early detection and family screening.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-black aspect-video mb-8">
              <video
                controls
                autoPlay
                className="w-full h-full"
                poster="https://readdy.ai/api/search-image?query=cardiologist%20explaining%20inherited%20heart%20conditions%20with%20anatomical%20heart%20model%20in%20modern%20medical%20office%20professional%20healthcare%20presentation%20warm%20lighting%20clean%20background&width=800&height=450&seq=fhd-video-poster-main&orientation=landscape"
              >
                <source
                  src="https://rr1---sn-nx57ynsd.googlevideo.com/videoplayback?expire=1771319036&ei=3BKUaaXJLca8n88PzMmlwQQ&ip=151.186.183.24&id=8e306562df8a383c&itag=22&source=contrib_service_notebooklm&begin=0&requiressl=yes&xpc=EghoqJzIP3oBAQ==&met=1771311836,&mh=fL&mm=32&mn=sn-nx57ynsd&ms=su&mv=m&mvi=1&pl=24&rms=su,su&sc=yes&susc=nblm&app=fife&ic=1045&eaua=vlflJW24Q14&pcm2=yes&mime=video/mp4&vprv=1&rqh=1&dur=355.636&lmt=1769285956483053&mt=1771311669&txp=0011224&sparams=expire,ei,ip,id,itag,source,requiressl,xpc,susc,app,ic,eaua,pcm2,mime,vprv,rqh,dur,lmt&sig=AJEij0EwRAIge21yO2KrfylB_if8m8J1kHppqOvglS90RtwMkEIgePcCIBkw8yf5FI1BLNwou6Vc4ACChsrslE778PmkdD5ewYkL&lsparams=met,mh,mm,mn,ms,mv,mvi,pl,rms,sc&lsig=APaTxxMwRgIhAJC1bWBpkhuwZ9Dhr7adKhW_mhf-50SUA57MA0_BRLisAiEA5GCnvcUeqZyaxBtcsn5RA_5YD3g0MeggARaF9W3P5UQ="
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Familial Heart Disease Series</h3>
                  <p className="text-sm text-gray-500">Duration: 5:55</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This video covers the essential information about inherited heart conditions including Hypertrophic Cardiomyopathy, Long QT Syndrome, Dilated Cardiomyopathy, and Marfan Syndrome. Learn how genetic screening can identify at-risk family members and how early treatment can prevent life-threatening cardiac events.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">More Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Familial Heart Diseases: A Visual Guide
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A comprehensive visual overview of inherited cardiovascular conditions—from genetics and risk factors to screening and treatment outcomes.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#fef9f9] rounded-2xl p-6 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/bf578d233a52b5ed4d3321f7cd123816.png"
                  alt="Familial Heart Diseases Infographic"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-dna-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Genetic Inheritance</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Most familial heart diseases follow autosomal dominant inheritance—50% chance if one parent is affected.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-stethoscope-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Early Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  ECGs, echocardiograms, and genetic tests can detect conditions years before symptoms appear.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-heart-pulse-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Treatable Conditions</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  With modern medicine, most inherited heart conditions are manageable, enabling full, active lives.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default FamilialHeartDiseases;
